import { useEffect, useState } from 'react';
import { Button, Drawer, Dropdown } from 'antd';
import { AppstoreOutlined, MenuOutlined } from '@ant-design/icons';
import { Link } from 'react-scroll';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

interface LandingNavProps {
  /** When true (on the landing page itself), Home scrolls to the hero. */
  onLanding?: boolean;
}

const featureLinks = [
  {
    to: '/features/whatsapp',
    title: 'WhatsApp for Schools',
    desc: 'Parents, teachers and drivers on one number',
  },
  {
    to: '/features/fees',
    title: 'Fees Management',
    desc: 'Invoices, arrears and automatic reminders',
  },
  {
    to: '/features/accounting',
    title: 'School Accounting',
    desc: 'Ledger, budgets and bank reconciliation',
  },
  {
    to: '/features/reports',
    title: 'Report Cards & Results',
    desc: 'ZIMSEC and Cambridge style grading',
  },
  {
    to: '/features/ecocash-payments',
    title: 'EcoCash Payments',
    desc: 'Fees paid and receipted automatically',
  },
];

/**
 * The single public-site navigation, shared by the landing page, news pages,
 * and the marketing feature pages. Owns the scrolled-background behaviour so
 * pages do not need their own scroll listeners, and provides the mobile
 * drawer menu below the 1100px breakpoint where the inline links are hidden.
 */
export const LandingNav: React.FC<LandingNavProps> = ({ onLanding = false }) => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);

  useEffect(() => {
    const nav = document.querySelector('.landing-main-nav');
    const handleScroll = () => {
      if (window.scrollY > 50) {
        nav?.classList.add('scrolled');
      } else {
        nav?.classList.remove('scrolled');
      }
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const featuresMenu = (
    <div className="landing-features-menu">
      {featureLinks.map((feature) => (
        <RouterLink key={feature.to} to={feature.to} className="landing-features-menu-link">
          <span className="landing-features-menu-title">{feature.title}</span>
          <span className="landing-features-menu-desc">{feature.desc}</span>
        </RouterLink>
      ))}
    </div>
  );

  return (
    <nav className="landing-main-nav">
      <div className="landing-nav-container">
        <a href="/" className="landing-nav-logo">
          <picture>
            <source srcSet="/Rongeka_web.webp" type="image/webp" />
            <img
              src="/Rongeka_web.png"
              alt="Rongeka Africa School Management System"
              className="landing-logo-icon"
            />
          </picture>
        </a>
        <ul className="landing-nav-center">
          <li>
            {onLanding ? (
              <Link to="hero" smooth={true} duration={500}>Home</Link>
            ) : (
              <RouterLink to="/">Home</RouterLink>
            )}
          </li>
          <li>
            <Dropdown
              popupRender={() => featuresMenu}
              trigger={['click']}
              rootClassName="landing-features-dropdown"
            >
              <a href="/features/whatsapp" onClick={(e) => e.preventDefault()}>
                <AppstoreOutlined className="landing-nav-menu-icon" /> Features
              </a>
            </Dropdown>
          </li>
          <li><RouterLink to="/pricing">Pricing</RouterLink></li>
          <li><RouterLink to="/news">News</RouterLink></li>
        </ul>
        <div className="landing-nav-actions">
          <Button type="primary" onClick={() => navigate('/get-started')} className="landing-nav-cta">
            Get Started
          </Button>
          <Button
            type="text"
            className="landing-nav-mobile-trigger"
            icon={<MenuOutlined />}
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          />
        </div>
      </div>

      <Drawer
        title="Menu"
        placement="right"
        width={300}
        open={mobileOpen}
        onClose={closeMobile}
        rootClassName="landing-mobile-drawer"
      >
        <div className="landing-mobile-menu">
          <RouterLink to="/" onClick={closeMobile}>Home</RouterLink>
          <div className="landing-mobile-menu-group">Features</div>
          <div className="landing-mobile-menu-sub">
            {featureLinks.map((feature) => (
              <RouterLink key={feature.to} to={feature.to} onClick={closeMobile}>
                {feature.title}
              </RouterLink>
            ))}
          </div>
          <RouterLink to="/pricing" onClick={closeMobile}>Pricing</RouterLink>
          <RouterLink to="/news" onClick={closeMobile}>News</RouterLink>
          <Button
            type="primary"
            block
            className="landing-nav-cta landing-mobile-menu-cta"
            onClick={() => {
              closeMobile();
              navigate('/get-started');
            }}
          >
            Get Started
          </Button>
        </div>
      </Drawer>
    </nav>
  );
};
